import React, { useState, useEffect, useRef } from 'react';
import { Search, CheckCircle, XCircle, AlertCircle, Download, Square, Loader2, Building2, MapPin, Calendar, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import useSEO from '../hooks/useSEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BtwCheckPage() {
  useSEO(
    'BTW Check | Europese BTW-nummers Controleren via VIES',
    'Controleer Europese BTW-nummers live via de officiële EU VIES dienst. Gratis tool voor ondernemers en boekhouders.'
  );

  const [countries, setCountries] = useState([]);
  const [activeTab, setActiveTab] = useState('single');
  
  // Single check state
  const [selectedCountry, setSelectedCountry] = useState('NL');
  const [vatNumber, setVatNumber] = useState('');
  const [singleResult, setSingleResult] = useState(null);
  const [singleLoading, setSingleLoading] = useState(false);
  
  // Bulk check state
  const [bulkInput, setBulkInput] = useState('');
  const [bulkResults, setBulkResults] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });
  const [bulkStopped, setBulkStopped] = useState(false);
  const stopRef = useRef(false);

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      const response = await fetch(`${API_URL}/api/vat/countries`);
      const data = await response.json();
      setCountries(data.countries);
    } catch (error) {
      console.error('Failed to fetch countries:', error);
    }
  };

  const cleanVatNumber = (input) => {
    return input.replace(/[\s.\-]/g, '').toUpperCase();
  };

  const handleSingleCheck = async (e) => {
    e?.preventDefault();
    const cleaned = cleanVatNumber(vatNumber);
    
    if (!cleaned) {
      toast.error('Voer een BTW-nummer in');
      return;
    }

    setSingleLoading(true);
    setSingleResult(null);

    try {
      const response = await fetch(`${API_URL}/api/vat/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          country_code: selectedCountry,
          vat_number: cleaned
        })
      });

      const data = await response.json();
      setSingleResult(data);

      if (data.error) {
        toast.error(data.error);
      } else if (data.valid) {
        toast.success('BTW-nummer is geldig');
      } else {
        toast.error('BTW-nummer is ongeldig');
      }
    } catch (error) {
      toast.error('Kon niet verbinden met de server');
      setSingleResult({
        valid: false,
        error: 'Netwerkfout - controleer je internetverbinding'
      });
    } finally {
      setSingleLoading(false);
    }
  };

  const handleBulkCheck = async () => {
    // Parse input - split by newlines, commas, or semicolons
    const lines = bulkInput
      .split(/[\n,;]+/)
      .map(line => line.trim())
      .filter(line => line.length > 0);

    if (lines.length === 0) {
      toast.error('Voer minstens één BTW-nummer in');
      return;
    }

    if (lines.length > 100) {
      toast.error('Maximum 100 BTW-nummers per keer');
      return;
    }

    setBulkLoading(true);
    setBulkStopped(false);
    stopRef.current = false;
    setBulkResults([]);
    setBulkProgress({ current: 0, total: lines.length });

    const results = [];

    for (let i = 0; i < lines.length; i++) {
      if (stopRef.current) {
        setBulkStopped(true);
        break;
      }

      const vatInput = cleanVatNumber(lines[i]);
      
      // Extract country code (first 2 chars if letters)
      let countryCode = '';
      let numberPart = vatInput;
      
      if (vatInput.length >= 2 && /^[A-Z]{2}/.test(vatInput)) {
        countryCode = vatInput.substring(0, 2);
        numberPart = vatInput.substring(2);
      }

      if (!countryCode) {
        results.push({
          input: lines[i],
          valid: false,
          country_code: '??',
          vat_number: vatInput,
          error: 'Geen landcode gevonden',
          check_date: new Date().toISOString()
        });
        setBulkResults([...results]);
        setBulkProgress({ current: i + 1, total: lines.length });
        continue;
      }

      try {
        const response = await fetch(`${API_URL}/api/vat/check`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            country_code: countryCode,
            vat_number: numberPart
          })
        });

        const data = await response.json();
        results.push({ ...data, input: lines[i] });
      } catch (error) {
        results.push({
          input: lines[i],
          valid: false,
          country_code: countryCode,
          vat_number: numberPart,
          error: 'Netwerkfout',
          check_date: new Date().toISOString()
        });
      }

      setBulkResults([...results]);
      setBulkProgress({ current: i + 1, total: lines.length });

      // Longer delay between requests to avoid VIES rate limiting
      if (i < lines.length - 1 && !stopRef.current) {
        await new Promise(resolve => setTimeout(resolve, 1500));
      }
    }

    setBulkLoading(false);
    
    if (!stopRef.current) {
      const validCount = results.filter(r => r.valid).length;
      toast.success(`Klaar! ${validCount} van ${results.length} geldig`);
    }
  };

  const handleStopBulk = () => {
    stopRef.current = true;
    toast.info('Controle wordt gestopt...');
  };

  const exportToCsv = () => {
    if (bulkResults.length === 0) return;

    const headers = ['BTW-nummer', 'Status', 'Bedrijfsnaam', 'Adres', 'Land', 'Datum controle', 'Foutmelding'];
    const rows = bulkResults.map(r => [
      `${r.country_code}${r.vat_number}`,
      r.error ? 'Fout' : (r.valid ? 'Geldig' : 'Ongeldig'),
      r.company_name || '',
      r.address?.replace(/,/g, ' ') || '',
      r.country_name || '',
      r.check_date?.split('T')[0] || '',
      r.error || ''
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `btw-check-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    
    toast.success('CSV geëxporteerd');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('nl-NL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr.split('T')[0];
    }
  };

  return (
    <div className="container mx-auto px-4 py-12 max-w-4xl">
      {/* Header */}
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-4 py-2 rounded-full text-sm font-medium mb-4">
          <Building2 className="w-4 h-4" />
          EU VIES Validatie
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold mb-3">BTW Check</h1>
        <p className="text-muted max-w-xl mx-auto">
          Controleer Europese BTW-nummers live via de officiële EU VIES dienst. 
          Gratis en direct resultaat.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('single')}
          className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all ${
            activeTab === 'single'
              ? 'bg-accent text-white'
              : 'bg-card border border-border hover:bg-muted/10'
          }`}
        >
          Enkele controle
        </button>
        <button
          onClick={() => setActiveTab('bulk')}
          className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all ${
            activeTab === 'bulk'
              ? 'bg-accent text-white'
              : 'bg-card border border-border hover:bg-muted/10'
          }`}
        >
          Bulkcontrole
        </button>
      </div>

      {/* Single Check Tab */}
      {activeTab === 'single' && (
        <div className="bg-card border border-border rounded-2xl p-6">
          <form onSubmit={handleSingleCheck} className="space-y-4">
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Land</label>
                <select
                  value={selectedCountry}
                  onChange={(e) => setSelectedCountry(e.target.value)}
                  className="w-full px-4 py-3 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-accent"
                >
                  {countries.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.code} - {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium mb-2">BTW-nummer</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={vatNumber}
                    onChange={(e) => setVatNumber(e.target.value)}
                    placeholder="123456789B01"
                    className="flex-1 px-4 py-3 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-accent font-mono placeholder:text-slate-400 dark:placeholder:text-slate-500"
                  />
                  <button
                    type="submit"
                    disabled={singleLoading}
                    className="bg-accent text-white px-6 py-3 rounded-xl font-medium hover:bg-accent/90 transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    {singleLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Search className="w-5 h-5" />
                    )}
                    Controleer
                  </button>
                </div>
              </div>
            </div>
            <p className="text-sm text-muted">
              Spaties, punten en streepjes worden automatisch verwijderd. De landcode vooraan mag worden weggelaten.
            </p>
          </form>

          {/* Single Result */}
          {singleResult && (
            <div className={`mt-6 p-6 rounded-xl border ${
              singleResult.error
                ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                : singleResult.valid
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                  : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
            }`}>
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                  singleResult.error
                    ? 'bg-yellow-100 dark:bg-yellow-900/50'
                    : singleResult.valid
                      ? 'bg-green-100 dark:bg-green-900/50'
                      : 'bg-red-100 dark:bg-red-900/50'
                }`}>
                  {singleResult.error ? (
                    <AlertCircle className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
                  ) : singleResult.valid ? (
                    <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                  ) : (
                    <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`font-bold text-lg ${
                      singleResult.error
                        ? 'text-yellow-700 dark:text-yellow-300'
                        : singleResult.valid
                          ? 'text-green-700 dark:text-green-300'
                          : 'text-red-700 dark:text-red-300'
                    }`}>
                      {singleResult.error ? 'Fout' : singleResult.valid ? 'Geldig' : 'Ongeldig'}
                    </span>
                    <span className="font-mono text-muted">
                      {singleResult.country_code}{singleResult.vat_number}
                    </span>
                  </div>

                  {singleResult.error ? (
                    <div className="space-y-2">
                      <p className="text-yellow-700 dark:text-yellow-300">{singleResult.error}</p>
                      <button
                        onClick={handleSingleCheck}
                        className="inline-flex items-center gap-2 text-sm text-yellow-700 dark:text-yellow-300 hover:underline"
                      >
                        <RefreshCw className="w-4 h-4" />
                        Opnieuw proberen
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2 text-sm">
                      {singleResult.company_name && (
                        <div className="flex items-start gap-2">
                          <Building2 className="w-4 h-4 text-muted mt-0.5 flex-shrink-0" />
                          <span className="font-medium">{singleResult.company_name}</span>
                        </div>
                      )}
                      {singleResult.address && (
                        <div className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 text-muted mt-0.5 flex-shrink-0" />
                          <span>{singleResult.address}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-muted">
                        <Calendar className="w-4 h-4" />
                        <span>Gecontroleerd: {formatDate(singleResult.check_date)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Bulk Check Tab */}
      {activeTab === 'bulk' && (
        <div className="bg-card border border-border rounded-2xl p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                BTW-nummers (max 100, met landcode)
              </label>
              <textarea
                value={bulkInput}
                onChange={(e) => setBulkInput(e.target.value)}
                placeholder="NL123456789B01
BE0123456789
DE123456789
..."
                rows={6}
                className="w-full px-4 py-3 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-accent font-mono text-sm placeholder:text-slate-400 dark:placeholder:text-slate-500"
                disabled={bulkLoading}
              />
              <p className="text-sm text-muted mt-2">
                Eén BTW-nummer per regel, of gescheiden door komma's of puntkomma's. 
                Elke regel moet beginnen met een landcode (bijv. NL, BE, DE).
              </p>
            </div>

            <div className="flex gap-3">
              {!bulkLoading ? (
                <button
                  onClick={handleBulkCheck}
                  className="bg-accent text-white px-6 py-3 rounded-xl font-medium hover:bg-accent/90 transition-colors flex items-center gap-2"
                >
                  <Search className="w-5 h-5" />
                  Alles controleren
                </button>
              ) : (
                <button
                  onClick={handleStopBulk}
                  className="bg-red-500 text-white px-6 py-3 rounded-xl font-medium hover:bg-red-600 transition-colors flex items-center gap-2"
                >
                  <Square className="w-5 h-5" />
                  Stoppen
                </button>
              )}

              {bulkResults.length > 0 && (
                <button
                  onClick={exportToCsv}
                  className="border border-border px-6 py-3 rounded-xl font-medium hover:bg-muted/10 transition-colors flex items-center gap-2"
                >
                  <Download className="w-5 h-5" />
                  Exporteer CSV
                </button>
              )}
            </div>

            {/* Progress bar */}
            {bulkLoading && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-muted">
                  <span>Voortgang</span>
                  <span>{bulkProgress.current} / {bulkProgress.total}</span>
                </div>
                <div className="h-2 bg-muted/20 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-accent transition-all duration-300"
                    style={{ width: `${(bulkProgress.current / bulkProgress.total) * 100}%` }}
                  />
                </div>
              </div>
            )}

            {bulkStopped && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4 text-yellow-700 dark:text-yellow-300">
                Controle gestopt na {bulkProgress.current} van {bulkProgress.total} nummers.
              </div>
            )}

            {/* Results table */}
            {bulkResults.length > 0 && (
              <div className="mt-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold">Resultaten ({bulkResults.length})</h3>
                  <div className="flex gap-4 text-sm">
                    <span className="text-green-600 dark:text-green-400">
                      ✓ {bulkResults.filter(r => r.valid).length} geldig
                    </span>
                    <span className="text-red-600 dark:text-red-400">
                      ✗ {bulkResults.filter(r => !r.valid && !r.error).length} ongeldig
                    </span>
                    <span className="text-yellow-600 dark:text-yellow-400">
                      ⚠ {bulkResults.filter(r => r.error).length} fout
                    </span>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-2">Status</th>
                        <th className="text-left py-3 px-2">BTW-nummer</th>
                        <th className="text-left py-3 px-2">Bedrijfsnaam</th>
                        <th className="text-left py-3 px-2 hidden sm:table-cell">Adres</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkResults.map((result, idx) => (
                        <tr key={idx} className="border-b border-border/50 hover:bg-muted/5">
                          <td className="py-3 px-2">
                            {result.error ? (
                              <span className="inline-flex items-center gap-1 text-yellow-600 dark:text-yellow-400">
                                <AlertCircle className="w-4 h-4" />
                                Fout
                              </span>
                            ) : result.valid ? (
                              <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                                <CheckCircle className="w-4 h-4" />
                                Geldig
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
                                <XCircle className="w-4 h-4" />
                                Ongeldig
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-2 font-mono">
                            {result.country_code}{result.vat_number}
                          </td>
                          <td className="py-3 px-2">
                            {result.error ? (
                              <span className="text-yellow-600 dark:text-yellow-400 text-xs">{result.error}</span>
                            ) : (
                              result.company_name || '-'
                            )}
                          </td>
                          <td className="py-3 px-2 hidden sm:table-cell text-muted truncate max-w-[200px]">
                            {result.address || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Info section */}
      <div className="mt-8 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-2xl p-6">
        <h3 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">Over de VIES dienst</h3>
        <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">
          Deze tool gebruikt de officiële EU VIES (VAT Information Exchange System) dienst om BTW-nummers 
          te valideren. De gegevens komen direct van de belastingdiensten van de EU-lidstaten.
        </p>
        <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
          <li>• Resultaten zijn real-time en officieel</li>
          <li>• Beschikbaarheid hangt af van de EU-servers</li>
          <li>• Sommige landen tonen geen bedrijfsgegevens (privacyregels)</li>
        </ul>
      </div>
    </div>
  );
}
